/**
 * Self-Report Damage Scoring System
 * Homeowners answer concrete, observable questions about their property condition.
 * Each answer maps to a point value. The total maps to FEMA EM damage categories.
 */

export interface DamageQuestion {
  id: string;
  label: string;
  icon: string;
  options: Array<{ value: number; label: string }>;
}

export const DAMAGE_QUESTIONS: DamageQuestion[] = [
  {
    id: 'habitability',
    label: 'Can you safely live in your home right now?',
    icon: 'fa-house-chimney',
    options: [
      { value: 0, label: 'Yes, everything is functional' },
      { value: 2, label: 'Yes, but with some issues (utilities out, minor leaks)' },
      { value: 5, label: 'No, it\'s not safe to stay' },
      { value: 8, label: 'I\'ve been told not to enter / it\'s condemned' },
    ],
  },
  {
    id: 'roof',
    label: 'What is the condition of your roof?',
    icon: 'fa-house-crack',
    options: [
      { value: 0, label: 'No visible damage' },
      { value: 2, label: 'Missing shingles or minor leaks' },
      { value: 5, label: 'Large sections missing or tarped' },
      { value: 8, label: 'Collapsed or mostly gone' },
    ],
  },
  {
    id: 'structure',
    label: 'Structural integrity (walls, foundation, framing)?',
    icon: 'fa-building-columns',
    options: [
      { value: 0, label: 'No cracks or shifting' },
      { value: 1, label: 'Cosmetic cracks in walls or drywall' },
      { value: 4, label: 'Visible cracks in exterior walls or foundation' },
      { value: 8, label: 'Walls leaning, shifted off foundation, or partially collapsed' },
    ],
  },
  {
    id: 'water',
    label: 'Water intrusion or flooding?',
    icon: 'fa-water',
    options: [
      { value: 0, label: 'None' },
      { value: 2, label: 'Minor — a few inches or small area' },
      { value: 5, label: 'Significant — multiple rooms, standing water' },
      { value: 7, label: 'Entire home flooded or submerged' },
    ],
  },
  {
    id: 'fire',
    label: 'Fire or smoke damage?',
    icon: 'fa-fire',
    options: [
      { value: 0, label: 'None' },
      { value: 2, label: 'Smoke damage or minor scorching' },
      { value: 5, label: 'One room or area burned' },
      { value: 8, label: 'Major structural fire damage' },
    ],
  },
  {
    id: 'utilities',
    label: 'Utility status (electric, gas, water, sewer)?',
    icon: 'fa-bolt',
    options: [
      { value: 0, label: 'All working' },
      { value: 1, label: 'One utility out' },
      { value: 3, label: 'Multiple utilities out' },
      { value: 5, label: 'All utilities out or hazardous (gas leak, exposed wiring)' },
    ],
  },
  {
    id: 'windows',
    label: 'Windows and doors?',
    icon: 'fa-door-open',
    options: [
      { value: 0, label: 'Intact' },
      { value: 1, label: 'Some broken windows' },
      { value: 3, label: 'Multiple openings breached, doors won\'t close' },
      { value: 5, label: 'Large structural openings exposed to elements' },
    ],
  },
];

export const MAX_SCORE = DAMAGE_QUESTIONS.reduce(
  (sum, q) => sum + Math.max(...q.options.map(o => o.value)),
  0
);

export function calculateScore(answers: Record<string, number>): number {
  return Object.values(answers).reduce((sum, val) => sum + (val || 0), 0);
}

export function scoreToCategory(score: number) {
  if (score <= 6) {
    return {
      category: 'affected',
      label: 'Affected',
      color: '#3B82F6',
      description: 'Livable with cosmetic or minor issues. May need some repairs.',
    };
  }
  if (score <= 18) {
    return {
      category: 'minor',
      label: 'Minor Damage',
      color: '#F59E0B',
      description: 'Damage is repairable. Home may still be habitable with caution.',
    };
  }
  if (score <= 32) {
    return {
      category: 'major',
      label: 'Major Damage',
      color: '#EF4444',
      description: 'Significant damage — likely uninhabitable until repaired.',
    };
  }
  return {
    category: 'destroyed',
    label: 'Destroyed',
    color: '#7C2D12',
    description: 'Total or near-total loss. Structure is not salvageable as-is.',
  };
}
